import { AnalyticsEvent } from "@omni/types/components/tracking/analyticsEvent";
import { Mediator } from "mediator-js";

import * as WindowManagement from "@/services/windowManagement";
import { WindowKeys } from "@/services/windowManagement";

import { deepCopy } from "../util/DeepCopy";

export type Subscriber = {
    name: string;
    id: string;
    fn: Function;
    channel: string;
};

export type SubscriberCallback = (
    eventName: string,
    event: AnalyticsEvent,
    context?: any
) => void;

export type TMediator = {
    subscribe: (
        channel: string,
        callback: SubscriberCallback,
        options?: any
    ) => Omit<Subscriber, "name">;
    publish: (
        channel: string,
        eventName: string,
        event: AnalyticsEvent,
        context?: any
    ) => void;
    remove: (channel: string, id: string) => void;
};

const NAMESPACE_TRACKING = "tracking";
const NAMESPACE_HISTORY = "tracking_history";

class EventDataLayer {
    private mediator: TMediator;
    private trackingHistory: AnalyticsEvent[];
    public trackingSubscribers: Subscriber[];

    constructor() {
        this.trackingSubscribers = [];
        this.trackingHistory = [];
        this.mediator = new Mediator();
    }

    public static getInstance(): EventDataLayer {
        if (!WindowManagement.getObject(WindowKeys.TRACKER)) {
            WindowManagement.saveObject(
                WindowKeys.TRACKER,
                new EventDataLayer()
            );
        }
        return WindowManagement.getObject(WindowKeys.TRACKER);
    }

    public clear() {
        this.trackingSubscribers.forEach((subscriber) =>
            this.mediator.remove(NAMESPACE_TRACKING, subscriber.id)
        );
        this.trackingSubscribers = [];
    }

    public subscribe(
        subscriberName: string,
        callback: SubscriberCallback,
        withHistory: boolean = false
    ) {
        if (!subscriberName || typeof callback !== "function") {
            throw new Error(`Invalid subscriber`);
        }

        this.trackingSubscribers.push({
            name: subscriberName,
            ...this.mediator.subscribe(NAMESPACE_TRACKING, callback)
        });

        if (withHistory) {
            this.runHistory(callback);
        }
    }

    public track(event: AnalyticsEvent) {
        const trackingEvent = deepCopy(event);
        this.mediator.publish(
            NAMESPACE_TRACKING,
            trackingEvent.event,
            trackingEvent
        );
        this.trackingHistory.push(trackingEvent);
    }

    private runHistory(callback: SubscriberCallback) {
        if (this.trackingHistory.length > 0) {
            // send history only for specific subscriber
            const predicate = (_eventName: string, _data: any, id: string) =>
                id === subscriber.id;
            const subscriber = this.mediator.subscribe(
                NAMESPACE_HISTORY,
                callback,
                { predicate }
            );

            this.trackingHistory.forEach((item) => {
                this.mediator.publish(
                    NAMESPACE_HISTORY,
                    item.event,
                    item,
                    subscriber.id
                );
            });

            this.mediator.remove(NAMESPACE_HISTORY, subscriber.id);
        }
    }
}

export default EventDataLayer;
